import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminAppLayout from "../../components/admin/AdminAppLayout";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useFormSubmitGuard } from "../../hooks/useFormSubmitGuard";

export default function AdminChangePasswordPage() {
  const { changePassword, user } = useAdminAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  useFormSubmitGuard(pending);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    setPending(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update password.");
    } finally {
      setPending(false);
    }
  }

  return (
    <AdminAppLayout>
      <header className="admin-app-pagehead admin-app-pagehead--split">
        <div>
          <p className="admin-dashboard-status">Admin</p>
          <h1 className="admin-app-title">Change password</h1>
        </div>
        <Link to="/admin" className="admin-app-back">
          Dashboard
        </Link>
      </header>

      <section className="admin-panel admin-panel--form">
        <p className="admin-login-hint">
          {user?.mustChangePassword
            ? "Your account is using a temporary password. Choose a new one before continuing."
            : "Enter your current password, then choose a new one."}
        </p>
        {error ? (
          <p className="admin-login-error" role="alert">
            {error}
          </p>
        ) : null}
        <form
          className="admin-login-form"
          onSubmit={handleSubmit}
          aria-busy={pending}
          data-submitting={pending ? "true" : undefined}
        >
          <fieldset className="admin-form-fieldset" disabled={pending}>
            <legend className="visually-hidden">New password</legend>
            <label htmlFor="admin-current-password">Current password</label>
            <input
              id="admin-current-password"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <label htmlFor="admin-new-password">New password</label>
            <input
              id="admin-new-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={10}
            />
            <label htmlFor="admin-confirm-password">Confirm new password</label>
            <input
              id="admin-confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={10}
            />
            <button type="submit">{pending ? "Saving…" : "Save password"}</button>
          </fieldset>
          {pending ? (
            <p className="form-status-text" role="status" aria-live="polite">
              Updating your password — please keep this page open.
            </p>
          ) : null}
        </form>
        <p className="admin-login-devnote">Use at least 10 characters for your new password.</p>
      </section>
    </AdminAppLayout>
  );
}
